import type { ITimezoneOption } from "~/utils/interfaces/timezones-interfaces";

/**
 * A step's zone is an IANA id stored next to a wall-clock time, never an offset: an offset loses the zone
 * and turns wrong at the next DST change. Display labels live in `timezone-labels.ts`.
 */

/** What a step without a zone reads as. */
export const DEFAULT_TIMEZONE = "Europe/Paris";

/** The same list under SSR: Node ships the same ICU. */
export function listTimezones(): string[] {
  return Intl.supportedValuesOf("timeZone");
}

/** Falls back to the default under SSR, where `resolvedOptions()` would report the server's zone. */
export function getBrowserTimezone(): string {
  if (!import.meta.client) return DEFAULT_TIMEZONE;

  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return detected && listTimezones().includes(detected) ? detected : DEFAULT_TIMEZONE;
}

/** The runtime's name for a zone (V8 lists `Europe/Kyiv` as `Europe/Kiev`), `null` if it doesn't know it. */
function canonicalTimezone(timezone: string): string | null {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: timezone }).resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

/**
 * The zone the JWT carries, the target's under a Log As. A token without one, or with a zone the runtime
 * doesn't know, falls back to `fallback`.
 */
export function resolveUserTimezone(jwtTimezone?: string | null, fallback: string = getBrowserTimezone()): string {
  return jwtTimezone && canonicalTimezone(jwtTimezone) ? jwtTimezone : fallback;
}

/** Not a strict match: a step may carry a zone's current name while the runtime lists its old one. */
export function findTimezoneOption(options: ITimezoneOption[], timezone: string): ITimezoneOption | null {
  const canonical = canonicalTimezone(timezone);

  return options.find((option) => option.value === timezone || option.value === canonical) ?? null;
}

/**
 * A missing zone reads as Paris, and a renamed zone is the same zone: neither may count as a change, which
 * would resend the ICS invites.
 */
export function isSameTimezone(first?: string | null, second?: string | null): boolean {
  const a = first ?? DEFAULT_TIMEZONE;
  const b = second ?? DEFAULT_TIMEZONE;
  if (a === b) return true;

  const canonical = canonicalTimezone(a);
  return !!canonical && canonical === canonicalTimezone(b);
}

/**
 * A zone's offset at an instant: read the instant in the zone, then read those figures back as UTC, since
 * `Intl` already carries the zone database. `% 24`: depending on ICU, `hour12: false` renders midnight as 24.
 */
function zoneOffsetMs(utcMs: number, timeZone: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(new Date(utcMs))
      .map((part) => [part.type, part.value]),
  );

  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );

  return asUtc - utcMs;
}

/**
 * Two passes, because the first offset is a guess that goes wrong around DST changes. A time that doesn't
 * exist (the spring-forward night) moves forward rather than failing, as calendars do.
 */
function wallTimeToInstant(date: string, time: string, timeZone: string): Date | null {
  const [year, month, day] = date.split(/[-T\s]/).map(Number);
  const [hours, minutes] = time.split(":").map(Number);

  if (!year || !month || !day || !Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  const naive = Date.UTC(year, month - 1, day, hours, minutes);
  const firstPass = zoneOffsetMs(naive, timeZone);
  let instant = naive - firstPass;

  const secondPass = zoneOffsetMs(instant, timeZone);
  if (secondPass !== firstPass) instant = naive - secondPass;

  return new Date(instant);
}

/** SDK `Date`s arrive at UTC midnight, so their day is the UTC day: local getters would shift it west of Greenwich. */
function toStepDay(date: string | Date): string {
  if (!(date instanceof Date)) return date;
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
}

/**
 * A step's date and time in the reader's zone, always with the date, since the conversion can change the day.
 * `null` for a step without hours (a bare date is never converted: it would move a day for half the globe)
 * and for a zone the runtime doesn't know, where `Intl` would throw. Callers then show the step as entered.
 */
export function toReaderDateTime(
  date: string | Date | null | undefined,
  time: string | null | undefined,
  timezone: string | null | undefined,
  readerTimezone: string,
): { date: string; time: string; isoDate: string } | null {
  if (!date || !time) return null;

  const stepTimezone = timezone ?? DEFAULT_TIMEZONE;
  if (!canonicalTimezone(stepTimezone) || !canonicalTimezone(readerTimezone)) return null;

  const instant = wallTimeToInstant(toStepDay(date), time, stepTimezone);
  if (!instant || Number.isNaN(instant.getTime())) return null;

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("fr-FR", {
      timeZone: readerTimezone,
      hour12: false,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
      .formatToParts(instant)
      .map((part) => [part.type, part.value]),
  );

  return {
    date: `${parts.day}/${parts.month}/${parts.year}`,
    time: `${Number(parts.hour) % 24 === 0 ? "00" : parts.hour}:${parts.minute}`,
    isoDate: `${parts.year}-${parts.month}-${parts.day}`,
  };
}
